import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Send, 
  Mail, 
  Phone, 
  MapPin,
  CheckCircle2,
  User,
  Building2,
  MessageSquare,
  Instagram,
  Twitter,
  Linkedin
} from 'lucide-react';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business: '',
    socialHandles: '',
    projectType: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const subject = `New Website Inquiry from ${formData.name}`;
    const body = `Hi Duke,

I'm interested in getting a custom website built.

Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone || 'Not provided'}
Business/Brand: ${formData.business}
Social Media Handles: ${formData.socialHandles || 'Not provided'}
Project Type: ${formData.projectType || 'Not specified'}

Message:
${formData.message}

Thanks!`;

    window.location.href = `mailto:duke.pettijohn@icloud.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0F]">
      {/* Header */}
      <header className="py-6 px-4 md:px-6 border-b border-[#2A2A35]">
        <div className="container mx-auto">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#FF6B35] to-[#FF8555] flex items-center justify-center">
                <span className="text-black font-bold">DP</span>
              </div>
              <span className="text-white font-semibold text-xl">DP Studios</span>
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-[#B8B8C0] hover:text-[#FF6B35] transition-colors"
            >
              <ArrowLeft size={18} />
              <span className="font-medium">Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-12 px-4 md:px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Left - Form */}
            <div>
              <span className="inline-block text-sm font-semibold text-[#FF6B35] uppercase tracking-wider mb-4">
                Get Started
              </span>
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Let's Build Something That{' '}
                <span className="gradient-text">Wins</span>
              </h1>
              <p className="text-lg text-[#B8B8C0] mb-8">
                Fill out the form below and I'll get back to you within 24 hours with a custom quote tailored to your goals.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name & Email Row */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Your Name *
                    </label>
                    <div className="relative">
                      <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B6B78]" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="John Smith"
                        className="w-full pl-12 pr-4 py-3 rounded-xl bg-[#12121A] border border-[#2A2A35] text-white placeholder-[#6B6B78] focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 outline-none transition-all"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Email Address *
                    </label>
                    <div className="relative">
                      <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B6B78]" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                        className="w-full pl-12 pr-4 py-3 rounded-xl bg-[#12121A] border border-[#2A2A35] text-white placeholder-[#6B6B78] focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 outline-none transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* Phone & Business Row */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Phone Number
                    </label>
                    <div className="relative">
                      <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B6B78]" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                        className="w-full pl-12 pr-4 py-3 rounded-xl bg-[#12121A] border border-[#2A2A35] text-white placeholder-[#6B6B78] focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 outline-none transition-all"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Business/Brand Name *
                    </label>
                    <div className="relative">
                      <Building2 size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B6B78]" />
                      <input
                        type="text"
                        name="business"
                        value={formData.business}
                        onChange={handleChange}
                        required
                        placeholder="Your Brand"
                        className="w-full pl-12 pr-4 py-3 rounded-xl bg-[#12121A] border border-[#2A2A35] text-white placeholder-[#6B6B78] focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 outline-none transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* Social Handles */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Social Media Handles
                  </label>
                  <div className="relative">
                    <Instagram size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6B6B78]" />
                    <input
                      type="text"
                      name="socialHandles"
                      value={formData.socialHandles}
                      onChange={handleChange}
                      placeholder="@yourhandle (Instagram, TikTok, etc.)"
                      className="w-full pl-12 pr-4 py-3 rounded-xl bg-[#12121A] border border-[#2A2A35] text-white placeholder-[#6B6B78] focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 outline-none transition-all"
                    />
                  </div>
                </div>

                {/* Project Type */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    What type of project?
                  </label>
                  <select
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl bg-[#12121A] border border-[#2A2A35] text-white focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 outline-none transition-all"
                  >
                    <option value="" className="bg-[#12121A]">Select a project type</option>
                    <option value="Personal Brand Website" className="bg-[#12121A]">Personal Brand Website</option>
                    <option value="Athlete/Creator Portfolio" className="bg-[#12121A]">Athlete/Creator Portfolio</option>
                    <option value="Business Landing Page" className="bg-[#12121A]">Business Landing Page</option>
                    <option value="Link-in-Bio Replacement" className="bg-[#12121A]">Link-in-Bio Replacement</option>
                    <option value="E-commerce/Shop" className="bg-[#12121A]">E-commerce/Shop</option>
                    <option value="Other" className="bg-[#12121A]">Other</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Tell me about your project *
                  </label>
                  <div className="relative">
                    <MessageSquare size={18} className="absolute left-4 top-4 text-[#6B6B78]" />
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="What are your goals? What do you need help with? Any specific features or ideas?"
                      className="w-full pl-12 pr-4 py-3 rounded-xl bg-[#12121A] border border-[#2A2A35] text-white placeholder-[#6B6B78] focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20 outline-none transition-all resize-none"
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="btn-primary w-full text-lg py-4"
                >
                  Send Inquiry
                  <Send size={20} />
                </button>

                <p className="text-sm text-[#6B6B78] text-center">
                  By submitting, your default email client will open with a pre-filled message.
                </p>
              </form>
            </div>

            {/* Right - Info */}
            <div className="lg:pt-16">
              {/* Contact Info Card */}
              <div className="bg-[#12121A] rounded-2xl p-8 border border-[#2A2A35] mb-8">
                <h3 className="text-xl font-semibold text-white mb-6">
                  Get in Touch Directly
                </h3>
                <div className="space-y-4">
                  <a 
                    href="mailto:duke.pettijohn@icloud.com"
                    className="flex items-center gap-4 p-4 rounded-xl hover:bg-[#1A1A24] transition-colors group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF6B35] to-[#FF8555] flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Mail size={22} className="text-black" />
                    </div>
                    <div>
                      <div className="text-sm text-[#6B6B78]">Email</div>
                      <div className="font-medium text-white">duke.pettijohn@icloud.com</div>
                    </div>
                  </a>
                  <a 
                    href="tel:+14693831834"
                    className="flex items-center gap-4 p-4 rounded-xl hover:bg-[#1A1A24] transition-colors group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF6B35] to-[#FF8555] flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Phone size={22} className="text-black" />
                    </div>
                    <div>
                      <div className="text-sm text-[#6B6B78]">Phone</div>
                      <div className="font-medium text-white">(469) 383-1834</div>
                    </div>
                  </a>
                  <div className="flex items-center gap-4 p-4 rounded-xl">
                    <div className="w-12 h-12 rounded-xl bg-[#1A1A24] border border-[#2A2A35] flex items-center justify-center">
                      <MapPin size={22} className="text-[#FF6B35]" />
                    </div>
                    <div>
                      <div className="text-sm text-[#6B6B78]">Based in</div>
                      <div className="font-medium text-white">Florida, USA</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* What to Expect */}
              <div className="bg-gradient-to-br from-[#12121A] to-[#1A1A24] rounded-2xl p-8 border border-[#2A2A35]">
                <h3 className="text-xl font-semibold text-white mb-6">
                  What to Expect
                </h3>
                <div className="space-y-4">
                  {[
                    'Response within 24 hours',
                    'Free consultation call',
                    'Custom quote based on your needs',
                    'No pressure, no obligations',
                    'Direct communication with me'
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <CheckCircle2 size={20} className="text-[#FF6B35] flex-shrink-0" />
                      <span className="text-[#B8B8C0]">{item}</span>
                    </div>
                  ))}
                </div>

                {/* Social Links */}
                <div className="mt-8 pt-6 border-t border-[#2A2A35]">
                  <p className="text-[#6B6B78] text-sm mb-4">Connect with me</p>
                  <div className="flex gap-3">
                    <a href="#" className="w-10 h-10 rounded-lg bg-[#0A0A0F] border border-[#2A2A35] flex items-center justify-center text-[#B8B8C0] hover:text-[#FF6B35] hover:border-[#FF6B35]/30 transition-colors">
                      <Instagram size={18} />
                    </a>
                    <a href="#" className="w-10 h-10 rounded-lg bg-[#0A0A0F] border border-[#2A2A35] flex items-center justify-center text-[#B8B8C0] hover:text-[#FF6B35] hover:border-[#FF6B35]/30 transition-colors">
                      <Twitter size={18} />
                    </a>
                    <a href="#" className="w-10 h-10 rounded-lg bg-[#0A0A0F] border border-[#2A2A35] flex items-center justify-center text-[#B8B8C0] hover:text-[#FF6B35] hover:border-[#FF6B35]/30 transition-colors">
                      <Linkedin size={18} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContactPage;
